import purchaseOrderEndpoints from '@/lib/api/endpoints/purchaseOrderEndpoints';
import type { PurchaseOrder } from '@/lib/models/purchaseOrder';
import { PurchaseOrderSchema } from '@/lib/models/purchaseOrder';
import type { PurchaseOrderIntegration } from '@/lib/models/purchaseOrderIntegration';
import { PurchaseOrderIntegrationSchema } from '@/lib/models/purchaseOrderIntegration';
import type { ODataResponse } from '@/lib/models/common';
import { zodValidator } from '@/lib/utils/zodValidator';

/**
 * Interfaccia per il service dei PurchaseOrder
 * Mantiene esattamente gli stessi nomi dei metodi del JavaScript originale
 * Wrapper che estrae automaticamente i dati dalle response Fetch
 */
export interface PurchaseOrdersService {
	/**
	 * Recupera la lista degli ordini di acquisto con query OData
	 * @param query - Query string OData
	 * @returns Promise con response OData contenente array di PurchaseOrder
	 */
	getPurchaseOrders(query: string): Promise<ODataResponse<PurchaseOrder>>;

	/**
	 * Recupera un singolo ordine di acquisto per ID
	 * @param id - ID dell'ordine di acquisto
	 * @returns Promise con singolo PurchaseOrder
	 */
	getPurchaseOrder(id: number): Promise<PurchaseOrder>;

	/**
	 * Aggiorna un ordine di acquisto esistente
	 * @param id - ID dell'ordine di acquisto da aggiornare
	 * @param payload - Dati da aggiornare
	 * @returns Promise con PurchaseOrder aggiornato
	 */
	updatePurchaseOrder(id: number, payload: PurchaseOrder): Promise<PurchaseOrder>;

	/**
	 * Crea un nuovo ordine di acquisto
	 * @param payload - Dati del nuovo ordine di acquisto
	 * @returns Promise con PurchaseOrder creato
	 */
	postPurchaseOrder(payload: PurchaseOrder): Promise<PurchaseOrder>;

	/**
	 * Elimina un ordine di acquisto
	 * @param id - ID dell'ordine di acquisto da eliminare
	 * @returns Promise void
	 */
	deletePurchaseOrder(id: number): Promise<void>;

	/**
	 * Recupera le integrazioni di un ordine di acquisto
	 * @param purchaseOrderId - ID dell'ordine di acquisto
	 * @returns Promise con array di PurchaseOrderIntegration
	 */
	getIntegrations(purchaseOrderId: number): Promise<PurchaseOrderIntegration[]>;
}

/**
 * Implementazione del service per i PurchaseOrder
 * Mantiene esattamente gli stessi nomi dei metodi del JavaScript originale
 * Estrae automaticamente la proprietà .data dalle response Fetch
 * Include validazione runtime centralizzata con zodValidator
 */
const purchaseOrdersService: PurchaseOrdersService = {
	async getPurchaseOrders(query: string): Promise<ODataResponse<PurchaseOrder>> {
		const response = (await purchaseOrderEndpoints.getPurchaseOrders(query)).data;
		// Validazione runtime centralizzata (attiva solo in development)
		zodValidator.validateODataResponse(response, PurchaseOrderSchema, 'PurchaseOrder.getAll');
		return response;
	},

	async getPurchaseOrder(id: number): Promise<PurchaseOrder> {
		const rawData = (await purchaseOrderEndpoints.getPurchaseOrder(id)).data;
		// Validazione runtime centralizzata (attiva solo in development)
		zodValidator.validateSingle(rawData, PurchaseOrderSchema, `PurchaseOrder.getById(${id})`);
		return rawData;
	},

	async updatePurchaseOrder(id: number, payload: PurchaseOrder): Promise<PurchaseOrder> {
		const rawData = (await purchaseOrderEndpoints.updatePurchaseOrder(id, payload)).data;
		// Validazione runtime centralizzata (attiva solo in development)
		zodValidator.validateSingle(rawData, PurchaseOrderSchema, `PurchaseOrder.updateById(${id})`);
		return rawData;
	},

	async postPurchaseOrder(payload: PurchaseOrder): Promise<PurchaseOrder> {
		const rawData = (await purchaseOrderEndpoints.postPurchaseOrder(payload)).data;
		// Validazione runtime centralizzata (attiva solo in development)
		zodValidator.validateSingle(rawData, PurchaseOrderSchema, 'PurchaseOrder.create');
		return rawData;
	},

	async deletePurchaseOrder(id: number): Promise<void> {
		return (await purchaseOrderEndpoints.deletePurchaseOrder(id)).data;
	},

	async getIntegrations(purchaseOrderId: number): Promise<PurchaseOrderIntegration[]> {
		const { data } = await purchaseOrderEndpoints.getIntegrations(purchaseOrderId);
		// Validazione runtime centralizzata per le integrazioni (attiva solo in development)
		zodValidator.validateODataResponse(
			data,
			PurchaseOrderIntegrationSchema,
			`PurchaseOrder.getIntegrations(${purchaseOrderId})`,
		);
		return data.value;
	},
};

export default purchaseOrdersService;
